import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
    Briefcase, MapPin, Clock, Building2, Search, Loader2, ChevronRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
    full_time: 'Full Time', part_time: 'Part Time', contract: 'Contract',
};
const LOCATION_LABELS = {
    wfh: 'Remote', office: 'On-site', hybrid: 'Hybrid',
};

export default function CareersPage() {
    const { orgSlug } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        axios.get(`${API}/public/careers/${orgSlug}`)
            .then(res => setData(res.data))
            .catch(() => setError('Careers page not found'))
            .finally(() => setLoading(false));
    }, [orgSlug]);

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAF5' }}>
            <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
        </div>
    );

    if (error || !data) return (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAF5' }}>
            <div className="text-center">
                <Briefcase className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-500">{error || 'Page not found'}</p>
            </div>
        </div>
    );

    const filteredJobs = data.jobs.filter(j =>
        !search || j.title.toLowerCase().includes(search.toLowerCase()) ||
        (j.department || '').toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#FAFAF5' }}>
            {/* Header */}
            <div className="bg-stone-900 text-white">
                <div className="max-w-4xl mx-auto px-6 py-16 text-center">
                    {data.org_logo && (
                        <img src={data.org_logo} alt={data.org_name} className="w-16 h-16 rounded-2xl mx-auto mb-4 object-cover" />
                    )}
                    <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                        Join {data.org_name}
                    </h1>
                    {data.description && <p className="text-stone-300 max-w-xl mx-auto">{data.description}</p>}
                    <p className="text-stone-400 mt-4 text-sm">{data.total_jobs} open position{data.total_jobs !== 1 ? 's' : ''}</p>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-6 py-8">
                {/* Search */}
                <div className="relative mb-8">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <input
                        value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Search positions..."
                        className="w-full pl-12 pr-4 py-3 text-base border border-stone-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10 shadow-sm"
                    />
                </div>

                {/* Job List */}
                {filteredJobs.length === 0 ? (
                    <div className="text-center py-16">
                        <Briefcase className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                        <p className="text-stone-500">{search ? 'No matching positions found' : 'No open positions at this time'}</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredJobs.map(job => (
                            <Link key={job.job_id} to={`/careers/${orgSlug}/jobs/${job.job_id}`}
                                className="block bg-white rounded-xl border border-stone-200 p-5 hover:shadow-md hover:border-stone-300 transition-all group">
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-stone-900 group-hover:text-stone-700 transition-colors"
                                            style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                                            {job.title}
                                        </h3>
                                        <div className="flex flex-wrap items-center gap-3 mt-2">
                                            {job.department && (
                                                <span className="text-xs text-stone-500 flex items-center gap-1">
                                                    <Building2 className="w-3.5 h-3.5" /> {job.department}
                                                </span>
                                            )}
                                            {job.location?.city && (
                                                <span className="text-xs text-stone-500 flex items-center gap-1">
                                                    <MapPin className="w-3.5 h-3.5" /> {job.location.city}{job.location.country ? `, ${job.location.country}` : ''}
                                                </span>
                                            )}
                                            <span className="text-xs text-stone-500 flex items-center gap-1">
                                                <Clock className="w-3.5 h-3.5" /> {TYPE_LABELS[job.employment_type] || job.employment_type}
                                            </span>
                                            <span className="inline-flex px-2 py-0.5 rounded-full bg-stone-100 text-xs text-stone-600">
                                                {LOCATION_LABELS[job.work_location] || job.work_location}
                                            </span>
                                        </div>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-stone-300 group-hover:text-stone-500 transition-colors" />
                                </div>
                            </Link>
                        ))}
                    </div>
                )}

                {/* Footer */}
                <div className="text-center mt-12 py-6 border-t border-stone-200">
                    <p className="text-xs text-stone-400">Powered by NurtureCRM</p>
                </div>
            </div>
        </div>
    );
}
